/**
 * Floating Sidebar Component
 * Phase 5: Displays Smart Swirls with AI color-theming
 */

import React, { useState, useEffect } from 'react';
import { Sparkles, X, RefreshCw, ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import SmartSwirlCard from './SmartSwirlCard';

export default function FloatingSidebar({ isOpen, onClose, onSwirlClick, activeColorPalette }) {
  const { user } = useAuth();
  const [smartSwirls, setSmartSwirls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [activeSwirlId, setActiveSwirlId] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    smartSwirls: true
  });
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    if (isOpen && user?.id) {
      loadSmartSwirls();
      
      // Restore active swirl from sessionStorage
      try {
        const lastId = sessionStorage.getItem('lastActiveSmartSwirlId');
        if (lastId) {
          setActiveSwirlId(lastId);
        }
      } catch (e) {
        // Ignore
      }
    }
  }, [isOpen, user?.id]);

  // Listen for moodsync:active events to sync active swirl
  useEffect(() => {
    const handleMoodSync = (event) => {
      const swirl = event.detail;
      if (swirl && swirl.id) {
        setActiveSwirlId(swirl.id);
        
        // Update CSS variable for accent color
        if (swirl.color_palette?.primary) {
          document.documentElement.style.setProperty(
            '--moodsync-accent',
            swirl.color_palette.primary
          );
        }
      }
    };

    window.addEventListener('moodsync:active', handleMoodSync);
    return () => {
      window.removeEventListener('moodsync:active', handleMoodSync);
    };
  }, []);

  const loadSmartSwirls = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .schema('deepsearch').from('smart_swirls')
        .select('*')
        .eq('user_id', user.id)
        .eq('enabled', true)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) {
        // Handle missing table gracefully
        if (error.code === 'PGRST116' || error.code === 'PGRST205' || error.message?.includes('does not exist')) {
          if (import.meta.env.DEV) {
            console.warn('⚠️ Smart Swirls table not available (expected if not set up)');
          }
          setSmartSwirls([]);
          return;
        }
        throw error;
      }

      setSmartSwirls(data || []);
    } catch (error) {
      console.error('Error loading Smart Swirls:', error);
      setSmartSwirls([]);
    } finally {
      setLoading(false);
    }
  };


  const handleRegenerate = async () => {
    if (!user?.id || generating) return;

    setGenerating(true);
    try {
      // Call Supabase Edge Function for Smart Swirls generation
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session) {
        throw new Error('Not authenticated');
      }

      const token = session.session.access_token;
      
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/smart-swirls-generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: user.id,
          force_regenerate: true // Manual regeneration deletes and recreates
        })
      });

      if (!response.ok) {
        throw new Error('Failed to regenerate Smart Swirls');
      }

      // Reload after regeneration
      await loadSmartSwirls();
      
      if (import.meta.env.DEV) {
        console.log('✅ Smart Swirls regenerated');
      }
    } catch (error) {
      console.error('Error regenerating Smart Swirls:', error);
      alert('Failed to regenerate Smart Swirls. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleSwirlSelect = (swirl) => {
    setActiveSwirlId(swirl.id);
    
    // Emit moodsync:active event (which triggers MoodSync background)
    window.dispatchEvent(
      new CustomEvent('moodsync:active', {
        detail: {
          id: swirl.id,
          title: swirl.title,
          color_palette: swirl.color_palette
        }
      })
    );
    
    // Call original onClick handler if provided
    if (onSwirlClick) {
      onSwirlClick(swirl);
    }
  };

  // Apply active color palette as sidebar glow
  const sidebarGlowStyle = activeColorPalette 
    ? {
        boxShadow: `0 0 40px ${activeColorPalette.gradient?.[0] || activeColorPalette.primary || '#A78BFA'}40`
      }
    : {};

  if (!isOpen && !isCollapsed) return null;

  return (
    <>
      {/* Collapsed Button */}
      {isCollapsed && (
        <button
          onClick={() => setIsCollapsed(false)}
          className="fixed right-0 top-1/2 -translate-y-1/2 w-8 h-16 bg-white/90 backdrop-blur-xl border-l border-y border-neutral-200 rounded-l-lg shadow-lg z-50 flex items-center justify-center hover:bg-white transition-all"
          title="Expand sidebar"
        >
          <ChevronLeft className="w-4 h-4 text-neutral-600" />
        </button>
      )}

      {/* Full Sidebar */}
      {!isCollapsed && (
        <div 
          className="fixed right-0 top-0 h-full w-80 bg-white/90 backdrop-blur-xl border-l border-neutral-200 shadow-2xl z-50 flex flex-col transition-all duration-300"
          style={sidebarGlowStyle}
        >
          {/* Header */}
          <div className="p-4 border-b border-neutral-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              <h2 className="font-semibold text-neutral-800">Swirlspace</h2>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setIsCollapsed(true)}
                className="w-8 h-8 rounded-lg hover:bg-neutral-100 flex items-center justify-center transition-colors"
                title="Collapse"
              >
                <ChevronRight className="w-4 h-4 text-neutral-600" />
              </button>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-lg hover:bg-neutral-100 flex items-center justify-center transition-colors"
                title="Close"
              >
                <X className="w-4 h-4 text-neutral-600" />
              </button>
            </div>
          </div>

      {/* Regenerate Button (Dev Mode Only) */}
      {import.meta.env.DEV && (
        <div className="p-4 border-b border-neutral-200">
          <button
            onClick={handleRegenerate}
            disabled={generating}
            className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
          >
            {generating ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Regenerating...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Regenerate Smart Swirls
              </>
            )}
          </button>
        </div>
      )}

          {/* Content - Scrollable */}
          <div className="flex-1 overflow-y-auto">
            {/* Smart Swirls Section */}
            <div className="border-b border-neutral-200">
              <button
                onClick={() => toggleSection('smartSwirls')}
                className="w-full p-4 flex items-center justify-between hover:bg-neutral-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-600" />
                  <span className="font-medium text-neutral-800">Smart Swirls</span>
                  {smartSwirls.length > 0 && (
                    <span className="text-xs text-neutral-500 bg-neutral-100 px-2 py-0.5 rounded-full">
                      {smartSwirls.length}
                    </span>
                  )}
                </div>
                {expandedSections.smartSwirls ? (
                  <ChevronUp className="w-4 h-4 text-neutral-500" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-neutral-500" />
                )}
              </button>

              {expandedSections.smartSwirls && (
                <div className="px-4 pb-4 space-y-3">
                  {loading ? (
                    <div className="text-center py-8 text-neutral-500">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto mb-2"></div>
                      <p className="text-xs">Loading...</p>
                    </div>
                  ) : smartSwirls.length === 0 ? (
                    <div className="text-center py-8 text-neutral-500">
                      <Sparkles className="w-8 h-8 mx-auto mb-2 text-neutral-300" />
                      <p className="text-xs mb-1">No Smart Swirls yet</p>
                      <p className="text-xs text-neutral-400">AI will create clusters as you save content</p>
                    </div>
                  ) : (
                    smartSwirls.map((swirl) => (
                      <SmartSwirlCard
                        key={swirl.id}
                        swirl={swirl}
                        isActive={activeSwirlId === swirl.id}
                        onClick={() => handleSwirlSelect(swirl)}
                      />
                    ))
                  )}
                </div>
              )}
            </div>

          </div>
        </div>
      )}
    </>
  );
}

